import React from 'react'
import classNames from 'classnames'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import './Button.scss'

/**
 * @import import { Button } from '@/components/buttons/Button'
 * @param {object} props
 * @param {boolean} [props.primary] Legacy alias for `brand`
 * @param {boolean} [props.brand] Solid theme-colored styling for the primary action
 * @param {boolean} [props.critical] Red outline styling for dangerous actions
 * @param {boolean} [props.destructive] Solid red fill for destructive confirmations
 * @param {boolean} [props.success] Solid green fill
 * @param {boolean} [props.basic] No background or border, minimal emphasis
 * @param {boolean} [props.text] Renders as plain text without button chrome
 * @param {boolean} [props.link] Text styling in the brand link color
 * @param {node} [props.children] Label content
 * @param {node} [props.content] Alternative label; wins over children when both are passed
 * @param {boolean} [props.circular] Fully rounded, for icon-only buttons
 * @param {boolean} [props.pill] Pill-shaped ends on a labelled button
 * @param {boolean} [props.square] Equal width and height; required on icon-only buttons
 * @param {boolean} [props.compact] Same as `small`
 * @param {string} [props.size] 'small' or 'xs' ('mini'/'tiny' map to xs) as an alternative to the flag props
 * @param {boolean} [props.small] Compact height
 * @param {boolean} [props.xs] Smallest height
 * @param {boolean} [props.active] Holds the pressed appearance
 * @param {node} [props.icon] Icon rendered beside the label
 * @param {string} [props.alignIcon] Side of the label the icon renders on: 'left' (default) or 'right'
 * @param {boolean} [props.fullWidth] Stretches to fill the container
 * @param {boolean} [props.disabled] Blocks interaction and dims the button
 * @param {boolean} [props.loading] Shows the built-in loader and blocks interaction; the label turns invisible but keeps the button's width
 * @param {node} [props.tooltip] Tooltip shown while enabled
 * @param {node} [props.disabledTooltip] Tooltip shown while disabled or loading, explaining why
 * @param {object} [props.tooltipProps] Extra InteractiveTooltip props, e.g. placement overrides
 */
export const Button = ({
  primary, // legacy - same as brand
  critical,
  destructive,
  children,
  content,
  brand,
  success,
  basic,
  text,
  link,
  circular,
  pill,
  square,
  compact,
  size,
  small,
  active,
  xs,
  icon,
  alignIcon = 'left',
  fullWidth,
  disabled,
  loading,
  tooltip,
  tooltipProps = {},
  disabledTooltip,
  className,
  ...props
}) => {
  const isBrand = brand || primary
  const isText = text || link
  const isDefault = !isBrand && !critical && !destructive && !success && !basic && !isText

  const button = (
    <button
      type="button"
      className={classNames('balto-button', className, {
        default: isDefault,
        brand: isBrand,
        critical,
        destructive,
        success,
        basic,
        text: isText,
        link,
        square,
        circular,
        'pill-radius': pill,
        disabled,
        loading,
        'full-width': fullWidth,
        small: small || size === 'small' || compact,
        xs: xs || size === 'xs' || size === 'mini' || size === 'tiny',
        active,
      })}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && <span className="balto-button-loader" />}
      {alignIcon !== 'right' && icon}
      {content || children}
      {alignIcon === 'right' && icon}
    </button>
  )

  if ((disabled || loading) && disabledTooltip) {
    return (
      <InteractiveTooltip content={disabledTooltip} placement="top" {...tooltipProps}>
        <span style={{ display: 'inline-block' }}>{button}</span>
      </InteractiveTooltip>
    )
  }

  if (!disabled && !loading && tooltip) {
    return (
      <InteractiveTooltip content={tooltip} placement="top" {...tooltipProps}>
        <span style={{ display: 'inline-block' }}>{button}</span>
      </InteractiveTooltip>
    )
  }

  return button
}

/**
 * @import import { ButtonGroup } from '@/components/buttons/Button'
 * @param {object} props
 * @param {node} props.children Buttons rendered together as one attached group
 */
export const ButtonGroup = ({ children, className, ...props }) => {
  return (
    <div className={classNames('balto-button-group', className)} {...props}>
      {children}
    </div>
  )
}

Button.Group = ButtonGroup
